import React from "react"
import PropTypes from "prop-types"
import { IntlContextConsumer } from "gatsby-plugin-react-intl"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

import { isMatchedIgnoredPaths } from "../helpers"

function Seo({
  description,
  keywords,
  noIndex,
  title,
  lang,
  socialShareTitleOpengraph,
  socialShareDescriptionOpengraph,
  socialShareImageOpengraph,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            keywords
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const metaKeywords = keywords?.join(`, `) || site.siteMetadata.keywords
  const searchEngineVisibility = noIndex
    ? {
        name: "robots",
        content: "noindex",
      }
    : {}

  return (
    <IntlContextConsumer>
      {({
        defaultLanguage,
        language: currentLocale,
        languages,
        ignoredPaths,
        messages,
        originalPath,
        redirectDefaultLanguageToRoot,
      }) => (
        <Helmet
          htmlAttributes={{
            lang: lang || currentLocale,
            itemScope: "",
            itemType: "http://schema.org/WebPage",
          }}
          title={title}
          titleTemplate={`%s | ${site.siteMetadata.title}`}
          meta={[
            {
              itemprop: `name`,
              content: `${title} | ${site.siteMetadata.title}`,
            },
            {
              name: `description`,
              content: metaDescription,
            },
            {
              name: `keywords`,
              content: metaKeywords,
            },
            {
              property: `og:title`,
              content:
                socialShareTitleOpengraph || messages.socialShareTitleOpengraph,
            },
            {
              property: `og:description`,
              content:
                socialShareDescriptionOpengraph ||
                messages.socialShareDescriptionOpengraph,
            },
            {
              property: `og:type`,
              content: `website`,
            },
            {
              property: `og:image`,
              content:
                socialShareImageOpengraph ||
                `${site.siteMetadata.siteUrl}/assets/images/share/bsdex-og-${currentLocale}.png`,
            },
            {
              property: `og:url`,
              content:
                (redirectDefaultLanguageToRoot &&
                  defaultLanguage === currentLocale) ||
                isMatchedIgnoredPaths(ignoredPaths, originalPath)
                  ? `${site.siteMetadata.siteUrl}${originalPath}`
                  : `${site.siteMetadata.siteUrl}/${currentLocale}${originalPath}`,
            },
            {
              name: `twitter:card`,
              content: `summary_large_image`,
            },
            {
              name: `twitter:site`,
              content: site.siteMetadata.author,
            },
            {
              name: `twitter:creator`,
              content: site.siteMetadata.author,
            },
            {
              name: `twitter:title`,
              content:
                socialShareTitleOpengraph || messages.socialShareTitleOpengraph,
            },
            {
              name: `twitter:description`,
              content:
                socialShareDescriptionOpengraph ||
                messages.socialShareDescriptionOpengraph,
            },
            {
              name: `twitter:image`,
              content:
                socialShareImageOpengraph ||
                `${site.siteMetadata.siteUrl}/assets/images/share/bsdex-og-${currentLocale}.png`,
            },
            {
              name: `ahrefs-site-verification`,
              content: `ffe6bde9793d9f57f56502a99c19eefea952222093f9ce95699ef141df5d35b9`,
            },
            {
              content: "app-id=1545552589",
              name: "apple-itunes-app",
            },
            searchEngineVisibility,
          ]}
        >
          <link
            rel="canonical"
            href={
              (redirectDefaultLanguageToRoot &&
                defaultLanguage === currentLocale) ||
              isMatchedIgnoredPaths(ignoredPaths, originalPath)
                ? `${site.siteMetadata.siteUrl}${originalPath}`
                : `${site.siteMetadata.siteUrl}/${currentLocale}${originalPath}`
            }
          />
          {!isMatchedIgnoredPaths(ignoredPaths, originalPath) &&
            languages.length > 1 &&
            languages.map((language) => (
              <link
                rel="alternate"
                href={
                  (redirectDefaultLanguageToRoot &&
                    defaultLanguage === language) ||
                  isMatchedIgnoredPaths(ignoredPaths, originalPath)
                    ? `${site.siteMetadata.siteUrl}${originalPath}`
                    : `${site.siteMetadata.siteUrl}/${language}${originalPath}`
                }
                hrefLang={language}
                key={language}
              />
            ))}
          {!isMatchedIgnoredPaths(ignoredPaths, originalPath) &&
            languages.length > 1 && (
              <link
                rel="alternate"
                href={`${site.siteMetadata.siteUrl}${
                  redirectDefaultLanguageToRoot ? `` : `/${defaultLanguage}`
                }${originalPath}`}
                hrefLang="x-default"
              />
            )}
          <script type="text/javascript">
            {`
              var _mtm = window._mtm = window._mtm || [];
              _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
              var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
              g.type='text/javascript'; g.async=true; g.src='${process.env.GATSBY_MATOMO_SERVER}/js/container_${process.env.GATSBY_MATOMO_CONTAINER_ID}.js'; s.parentNode.insertBefore(g,s);
            `}
          </script>
        </Helmet>
      )}
    </IntlContextConsumer>
  )
}

Seo.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  description: ``,
}

Seo.propTypes = {
  lang: PropTypes.string,
  description: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
}

export default Seo
